/**
 * Device details for the download audit trail.
 *
 * =============================================================================
 * WHAT A BROWSER CANNOT TELL YOU
 * =============================================================================
 * MAC address, machine name, OS username, disk/BIOS serials - none of these are
 * exposed to a web page by any browser, and there is no workaround. Reading
 * them needs software installed on the candidate's machine.
 *
 * What follows is the closest the platform allows: a device id this app mints
 * itself, plus coarse hardware and browser characteristics. Everything here is
 * SELF-REPORTED - it travels in the request body, so anyone editing the request
 * can change it. The backend stores it under `client` in `userjson`, kept apart
 * from what the server observed for itself, so an investigation can tell the
 * two apart. Treat it as corroborating detail, never as proof.
 */

const DEVICE_ID_KEY = "mhcet_device_id";

/**
 * A stable id for this browser profile, generated locally and kept in
 * localStorage. More reliable than a fingerprint for "same device as before",
 * because fingerprints shift with every browser update.
 *
 * It is cleared by clearing site data and is per-browser, not per-machine, so
 * it answers "same browser profile" - not "same laptop".
 */
const getDeviceId = () => {
  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (error) {
    // Private mode / storage disabled - the rest of the payload still stands.
    return null;
  }
};

/**
 * The GPU string, e.g. "ANGLE (NVIDIA GeForce GTX 1650 Direct3D11 ...)".
 * The nearest thing to "which laptop" the browser will give up, though Firefox
 * and Safari may mask it. Wrapped because creating a WebGL context can throw on
 * machines with no GPU access.
 */
const getGpu = () => {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return null;

    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (!ext) return null;

    return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || null;
  } catch (error) {
    return null;
  }
};

/**
 * Collect what this browser will disclose.
 *
 * Async because User-Agent Client Hints only yields platform/architecture
 * details through a promise. Never rejects - a download must not fail because
 * a diagnostic value was unavailable.
 *
 * @returns {Promise<object>} flat object of scalars, safe to send as JSON
 */
export const collectDeviceInfo = async () => {
  const info = {
    deviceId: getDeviceId(),
    userAgent: navigator.userAgent || null,
    language: navigator.language || null,
    languages: Array.isArray(navigator.languages) ? navigator.languages : null,
    platform: navigator.platform || null,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    deviceMemory: navigator.deviceMemory || null,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    screen:
      window.screen && window.screen.width
        ? `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`
        : null,
    colorDepth: window.screen ? window.screen.colorDepth : null,
    gpu: getGpu(),
  };

  try {
    info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    info.timezoneOffset = new Date().getTimezoneOffset();
  } catch (error) {
    info.timezone = null;
  }

  // Chromium only. `platform` here is the real OS name, where
  // navigator.platform is increasingly frozen//misleading.
  try {
    if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
      const hints = await navigator.userAgentData.getHighEntropyValues([
        "platform",
        "platformVersion",
        "architecture",
        "bitness",
        "model",
      ]);
      info.uaPlatform = hints.platform || null;
      info.uaPlatformVersion = hints.platformVersion || null;
      info.uaArchitecture = hints.architecture || null;
      info.uaBitness = hints.bitness || null;
      info.uaModel = hints.model || null;
      info.mobile = !!navigator.userAgentData.mobile;
    }
  } catch (error) {
    // Hints unavailable - the rest of the payload is unaffected.
  }

  return info;
};

export default collectDeviceInfo;
